import React from 'react'
import { useNavigate } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faSearch, faFilter } from '@fortawesome/free-solid-svg-icons'
import DropDown from 'sr/helpers/ui-components/DropDown.tsx'

type Tab = { key: string; label: React.ReactNode }

type ListingHeaderProps = {
  title?: React.ReactNode
  tabs: Tab[]
  activeKey: string
  onTabChange: (key: string) => void
  search: string
  onSearchChange: (val: string) => void
  sortBy: string
  onSortChange: (val: string) => void
  primaryButton?: React.ReactNode
  className?: string
  sortOptions?: { value: string; label: string }[]
  searchPlaceholderId?: string
}

export const ListingHeader: React.FC<ListingHeaderProps> = ({
  title,
  tabs,
  activeKey,
  onTabChange,
  search,
  onSearchChange,
  sortBy,
  onSortChange,
  primaryButton,
  className,
  sortOptions = [
    { value: '', label: '—' },
    { value: 'title', label: 'Title A–Z' },
    { value: '-title', label: 'Title Z–A' },
    { value: '-date', label: 'Newest' },
    { value: 'date', label: 'Oldest' },
  ],
  searchPlaceholderId = 'SEARCH.PLACEHOLDER.GENERIC',
}) => {
  const intl = useIntl()
  const navigate = useNavigate()

  return (
    <div className={`bg-white rounded-xl p-5 mb-6 border border-gray-200 shadow-sm ${className || ''}`}>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors text-gray-600 hover:text-gray-800"
            title={intl.formatMessage({ id: 'BACK', defaultMessage: 'Back' })}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
          </button>
          <h1 className="text-xl font-semibold text-gray-800">
            {title || <FormattedMessage id="LISTING.TITLE" defaultMessage="Listing" />}
          </h1>
        </div>
        <div className="flex items-center space-x-2">{primaryButton}</div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex rounded-xl border border-gray-200 overflow-hidden w-fit">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => onTabChange(t.key)}
              className={`px-5 py-2.5 text-sm font-medium transition-colors flex items-center ${
                activeKey === t.key ? 'bg-[#ff39b3] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
          <div className="relative w-full sm:w-64">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FontAwesomeIcon icon={faSearch} className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={intl.formatMessage({ id: searchPlaceholderId, defaultMessage: 'Search…' })}
              className="pl-10 w-full h-10 border border-gray-300 rounded-xl bg-gray-50 focus:bg-white text-sm text-gray-700 placeholder-gray-500 focus:border-sky-200 focus:ring-sky-200 focus:outline-none focus:ring focus:ring-opacity-40"
            />
          </div>

          <div className="flex items-center">
            <div className="pr-3 text-sm text-gray-600 flex items-center">
              <FontAwesomeIcon icon={faFilter} className="h-3.5 w-3.5 mr-1.5" />
              <span className="whitespace-nowrap">
                <FormattedMessage id="SORT.BY" defaultMessage="Sort:" />
              </span>
            </div>
            <DropDown
              value={sortBy}
              onChange={(e) => onSortChange(e.target.value)}
              dropdowndiv="mt-0"
              className="rounded-xl border-gray-300 h-10 bg-gray-50 focus:bg-white"
              labelMargin="pb-0"
            >
              {sortOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </DropDown>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ListingHeader

